import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { ReactSVG } from 'react-svg';

import { getApartmentsData } from 'api/apartments.api';
import Furnitures from 'data/project/furn.svg';
import { Apartment } from 'types/Apartments';
import { importAllFiles } from 'utils/importAllFiles';
import { importApartmentsFromEdifice } from 'utils/importApartmentsFromEdifice';
import { importProjectData } from 'utils/importProjectData';
import { parseApartmentsToFloors } from 'utils/parseApartmentsToFloors';
import 'data/edifices/A/apartments/aprt.css';
// Here Add more .css files from data/edifices/*/tiers/tier.css

export interface DataContext {
  externalApartmentsData: any[];
  dataLoading: boolean;
  apartments: Apartment[];
  maxRooms: number;
  apartmentsPerFloorAmount: any;
  projectData: any;
  apartmentsImages: any[];
}

interface DataContextProps {
  children: ReactNode;
}

export const DataContext = createContext<DataContext>({
  externalApartmentsData: [],
  dataLoading: false,
  apartments: [],
  maxRooms: 0,
  apartmentsPerFloorAmount: {},
  projectData: {},
  apartmentsImages: [],
});

export const DataProvider = ({ children }: DataContextProps) => {
  const [externalApartmentsData, setExternalApartmentsData] = useState<any[]>(
    [],
  );
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const apartments: Apartment[] = useMemo(importApartmentsFromEdifice, []);

  const maxRooms = useMemo(
    () => Math.max(...apartments.map(a => a.rooms)),
    [apartments],
  );

  const apartmentsPerFloorAmount = useMemo(
    () => parseApartmentsToFloors(apartments),
    [apartments],
  );

  const projectData = useMemo(importProjectData, []);

  const apartmentsImages = useMemo(() => {
    const aBuilidingImages = importAllFiles(
      require.context('data/edifices/A/apartments', false, /\.(svg)$/),
    );

    // Uncomment if two buildings
    // const bBuilidingImages = importAllFiles(
    //   require.context('data/edifices/B/apartments', false, /\.(svg)$/),
    // );

    // @ts-ignore
    const uniqueAImages = [...new Set(aBuilidingImages)];
    // Uncomment if two buildings
    // @ts-ignore
    // const uniqueBImages = [...new Set(bBuilidingImages)];

    return [
      ...uniqueAImages,
      // Uncomment if two buildings
      // ...uniqueBImages,
    ];
  }, []);

  const completeApartmentsRequest = (response: any[]) => {
    setExternalApartmentsData(response);
    setDataLoading(false);
  };

  useEffect(() => {
    setDataLoading(true);
    getApartmentsData().then(completeApartmentsRequest);
  }, []);

  return (
    <DataContext.Provider
      value={{
        externalApartmentsData,
        dataLoading,
        apartments,
        maxRooms,
        apartmentsPerFloorAmount,
        projectData,
        apartmentsImages,
      }}
    >
      {children}
      <ReactSVG
        src={Furnitures}
        renumerateIRIElements={false}
        style={{ display: 'none' }}
      />
    </DataContext.Provider>
  );
};
